import { Box, Button } from '@material-ui/core';
import { Lock } from '@material-ui/icons';
import toggleNotification from 'actions/notifications';
import { Action, Resource } from 'components/Authorization';
import {
  useCanAccess,
  useHasFullResourceAccess
} from 'components/Authorization/utils';
import BtnWrapper from 'components/BtnWrapper';
import ContentBoxItem from 'components/ContentBoxItem';
import SideContent from 'components/SideContent';
import TitleWrapper from 'components/TitleWrapper';
import i18n from 'i18n';
import { createAssignment, fetchStoryStates } from 'pages/assignments/actions';
import DialogForm from 'pages/assignments/components/DialogForm';
import { defaultState, FormData } from 'pages/assignments/formData';
import {
  fetchRundown,
  fetchRundownStories,
  updateRundown
} from 'pages/rundowns/actions';
import { buildRundownStoryObject } from 'pages/rundowns/utils';
import {
  createStoryAsset,
  fetchStory,
  updateStory
} from 'pages/stories/actions';
import StoryBrowseAccordions from 'pages/stories/components/StoryBrowseAccordions';
import StoryFormTabs from 'pages/stories/components/StoryFormTabs';
import { StoryState } from 'pages/stories/enums';
import { StoryFormData } from 'pages/stories/interface';
import { AssetDynamic } from 'pages/stories/interfaces/assetDynamic';
import { ImageAsset } from 'pages/stories/interfaces/imageAsset';
import validateForm, { getInitialState, reducer } from 'pages/stories/utils';
import {
  buildChildren,
  calculateRelativeTime,
  convertArrayToAsset,
  convertAssetToArray,
  isSelfChild,
  setChildRelativeTime,
  setParentRelativeTime
} from 'pages/stories/utils/asset';
import {
  ChangeEvent,
  FunctionComponent,
  useCallback,
  useEffect,
  useReducer,
  useRef,
  useState
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router';
import { isFormValid } from 'utils/form';

const Edit: FunctionComponent = () => {
  const { guid, rundownGuid } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [storyLock, setStoryLock] = useState(false);
  const [storyApproved, setStoryApproved] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [isUserStoryOwner, setIsUserStoryOwner] = useState(false);
  const [calculatedDuration, setCalculatedDuration] = useState<number>(1);
  const [storyInitState, setStoryInitState] = useState<$Lns.StoryState>();
  const [editAssignment, setEditAssignment] = useState<FormData>();
  const isUserAdmin = useHasFullResourceAccess('STORY');
  const canApprove = useCanAccess(Resource.STORY, Action.APPROVE);

  const { storyStates, currentLnsUser, taskStates, currentSsoUser } =
    useSelector((globalState: $Lns.DefaultState) => globalState.default);

  const [state, dispatchReducer] = useReducer(reducer, {
    story: undefined,
    formData: getInitialState(),
    rundown: undefined
  });

  const formDataRef = useRef(state.formData);
  const [dynamicAsset, setDynamicAsset] = useState<AssetDynamic[]>([]);

  useEffect(() => {
    formDataRef.current = state.formData;
  }, [state.formData]);

  useEffect(() => {
    const assetsNew = [...dynamicAsset];
    let relativeTime = 0;
    assetsNew.forEach((asset, key) => {
      const itSelfChild = isSelfChild(asset, assetsNew, key);

      if (itSelfChild === undefined) {
        const childObjects = buildChildren(asset, assetsNew, key);
        relativeTime += calculateRelativeTime(asset, childObjects);
      }
    });

    setCalculatedDuration(relativeTime);
  }, [dynamicAsset]);

  const handleStoryUpdate = (
    data: StoryFormData,
    hitUpdateRundown: boolean
  ) => {
    updateStory(data)
      .then(({ data: jsonData, code }) => {
        if (code === 201) {
          dispatchReducer({ type: 'setStory', payload: jsonData });
          dispatch(
            toggleNotification(
              i18n.t('notifications.stories.updateSuccess'),
              'success'
            )
          );
          if (!hitUpdateRundown) return;
          handleRundownStoriesUpdate(jsonData);
        }
      })
      .catch(() =>
        dispatch(toggleNotification(i18n.t('notifications.apiError'), 'error'))
      );
  };

  const updateStoryState = (data: $Lns.Story, states?: $Lns.StoryState[]) => {
    if (storyStates.length > 0 || states !== undefined) {
      const storyStatesArray = states || storyStates;

      const stateNow = storyStatesArray.find(
        pin => pin.guid === data.storyState
      );

      setStoryInitState(stateNow);
      const stateLock = storyStatesArray.find(
        pin => pin.name === StoryState.locked
      );

      if (
        stateNow &&
        stateLock &&
        stateNow.name !== StoryState.locked &&
        stateNow.name !== StoryState.approved
      ) {
        const formDataUpdateState = {
          ...getInitialState(data),
          storyState: stateLock.guid
        };
        handleStoryUpdate(formDataUpdateState, false);
      } else {
        setStoryLock(true);

        if (stateNow && stateNow.name === StoryState.approved) {
          setStoryApproved(true);
        }

        dispatch(
          toggleNotification(
            i18n.t('pages.stories.helperText.storyLockMsg'),
            'warning'
          )
        );
      }
    } else if (storyStates.length === 0) {
      fetchStoryStates().then(resp => {
        updateStoryState(data, resp.data);
      });
    }
  };

  const checkStoryLock = (storyData: StoryFormData) => {
    const formData = { ...storyData };
    fetchStory(guid).then(({ data }) => {
      const stateLock = storyStates.find(pin => pin.name === StoryState.locked);
      if (stateLock && data.storyState === stateLock.guid) {
        updateStoryState(data);
      } else {
        if (storyInitState) formData.storyState = storyInitState.guid;
        handleStoryUpdate(formData, true);
      }
    });
  };

  const onUpdateDynamicAssets = (assetsNew: AssetDynamic[]) => {
    const sortedAssets = sortAssetsBeforeSet(assetsNew);
    const assetsTemp = setParentRelativeTime(sortedAssets);
    const assetsWithRelativeTime = setChildRelativeTime(assetsTemp);

    setDynamicAsset([...assetsWithRelativeTime]);
  };

  const sortAssetsBeforeSet = (assetsNew: AssetDynamic[]) => {
    const arrayFieldsAll = convertAssetToArray(assetsNew);
    arrayFieldsAll.sort((a, b) => {
      return a.positionInScript - b.positionInScript;
    });
    const sortedAssets = convertArrayToAsset(arrayFieldsAll);
    return sortedAssets;
  };

  useEffect(() => {
    fetchStory(guid).then(({ data }) => {
      dispatchReducer({ type: 'setStory', payload: data });
      dispatchReducer({
        type: 'setFormData',
        payload: getInitialState(data)
      });
      updateStoryState(data);
      if (data.createdBy === currentSsoUser.guid) setIsUserStoryOwner(true);
      if (data.script !== '') {
        const scriptItems = JSON.parse(data.script);
        const newDynamicAssets = convertArrayToAsset(scriptItems);
        onUpdateDynamicAssets([...newDynamicAssets]);
      }
      if (rundownGuid) {
        fetchRundown(rundownGuid).then(({ data: [newRundown] }) => {
          fetchRundownStories(newRundown.guid).then(({ data: storyData }) => {
            const stories = storyData;

            const updatedRundown = {
              ...newRundown,
              stories: stories.map((currentStory, index) => {
                return buildRundownStoryObject(
                  currentStory.story,
                  index,
                  currentStory.isSpare
                );
              })
            };

            const updatedFormData = {
              ...getInitialState(data),
              errors: {
                ...state.formData.errors,
                plannedDuration: i18n.t('helperText.required')
              }
            };
            dispatchReducer({ type: 'setFormData', payload: updatedFormData });
            dispatchReducer({ type: 'setRundown', payload: updatedRundown });
          });
        });
      }
    });
    // eslint-disable-next-line
  }, [dispatchReducer, guid, rundownGuid]);

  const onEditorChange = useCallback(
    (text: string, field: string) => {
      const updatedFormData = {
        ...formDataRef.current,
        errors: {
          [field]: validateForm(field, text)
        },
        [field]: text
      };

      return dispatchReducer({ type: 'setFormData', payload: updatedFormData });
    },
    [formDataRef]
  );

  if (!state.story) return <></>;

  const getFieldValue = (name: string, value: string) => {
    if (name === 'plannedDuration' || name === 'duration') {
      return parseInt(value, 10);
    }
    return value;
  };

  const handleRundownStoriesUpdate = (updatedStory: $Lns.Story) => {
    if (!state.rundown || !state.formData.guid || !state.rundown.stories)
      return;
    if (
      !state.rundown.stories
        ?.map(currentStory => currentStory.guid)
        .includes(state.formData.guid)
    ) {
      // update rundown and add story
      const updatedRundown = {
        ...state.rundown,
        stories: [
          ...state.rundown.stories,
          buildRundownStoryObject(
            updatedStory,
            state.rundown.stories.length,
            true
          )
        ]
      };

      updateRundown(updatedRundown).then(({ code }) => {
        if (code === 201) {
          dispatch(
            toggleNotification(
              i18n.t('notifications.rundowns.updateSuccess'),
              'success'
            )
          );
        }
        navigate(`/rundowns/${updatedRundown.guid}`);
      });
    }
  };

  const onChangeType = (e: ChangeEvent<{ name?: string; value: unknown }>) => {
    const { value, name } = e.target;

    if (name === 'pool') {
      return onUpdatePool(value as string[]);
    }

    const updatedFormData = {
      ...state.formData,
      [name || '']: value
    };

    return dispatchReducer({ type: 'setFormData', payload: updatedFormData });
  };

  const onChangeField = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    const updatedFormData = {
      ...state.formData,
      errors: {
        [name]: validateForm(name, value)
      },
      [name]: getFieldValue(name, value)
    };

    return dispatchReducer({ type: 'setFormData', payload: updatedFormData });
  };

  const onUpdatePool = (poolArray: string[]) => {
    if (!state.formData.pools) return;

    const poolGuid = poolArray[poolArray.length - 1];

    const existing = state.formData.pools?.find(p => p.guid === poolGuid);
    let pools: { guid: string }[] = [];

    if (existing) {
      pools = state.formData.pools.filter(p => p.guid !== poolGuid);
    } else {
      pools = [...state.formData.pools, { guid: poolGuid }];
    }

    const updatedFormData = {
      ...state.formData,
      pools
    };

    dispatchReducer({ type: 'setFormData', payload: updatedFormData });
  };

  const onSave = async () => {
    const output = await createAssets();

    const inputAssets = dynamicAsset.map(itemAsset => {
      const returnItem = { ...itemAsset } as AssetDynamic;
      if (returnItem.assetI) {
        const urlObj = new URL(returnItem.assetI.proxyUrl);

        const fileName = urlObj.pathname.substring(
          urlObj.pathname.lastIndexOf('/') + 1
        );

        if (output && output.length > 0) {
          const exist = output.find(item => {
            return item.sourceUrn === fileName;
          });

          if (exist && exist.guid) {
            returnItem.assetI.guid = exist.guid;
          }
        }
      }
      return returnItem;
    });

    const arrayFieldsAll = convertAssetToArray(inputAssets);

    const formDataScripted = {
      ...state.formData,
      calculatedDuration: Math.round(calculatedDuration),
      script: JSON.stringify([...arrayFieldsAll])
    };

    if (storyLock && storyInitState) {
      formDataScripted.storyState = storyInitState.guid;
    }

    if (
      !storyLock &&
      state.story &&
      storyInitState &&
      state.story.storyState === storyInitState.guid
    ) {
      checkStoryLock(formDataScripted);
    } else {
      handleStoryUpdate(formDataScripted, true);
    }
  };

  const createAssets = async () => {
    const inputAssetListList = [] as $Lns.CreateAsset[];
    dynamicAsset.forEach(itemAsset => {
      if (itemAsset.assetI) {
        const urlObj = new URL(itemAsset.assetI.proxyUrl);

        const fileName = urlObj.pathname.substring(
          urlObj.pathname.lastIndexOf('/') + 1
        );

        const objItem = {
          name: itemAsset.assetI.title,
          description: itemAsset.assetI?.description || 'Description',
          sourceUrn: fileName,
          assetType: itemAsset.assetI.type,
          renderUrl: itemAsset.assetI.proxyUrl,
          playoutServerUrl: itemAsset.assetI.playoutServerUrl,
          durationSec: +itemAsset.assetI.durationInSeconds || 100,
          clipStartSec: itemAsset.assetI?.clipStartSec || 100,
          clipStopSec: itemAsset.assetI?.clipStopSec || 100,
          playoutTimepointSec: itemAsset?.assetI.playoutTimepointSec || 100,
          totalDurationSec: itemAsset.assetI?.totalDurationSec || 100
        };

        inputAssetListList.push(objItem);
      }
    });

    const input = {
      story: guid,
      assets: [...inputAssetListList]
    };

    return await createStoryAsset(input)
      .then(res => {
        dispatch(
          toggleNotification(
            i18n.t('notifications.stories.attachSuccess'),
            'success'
          )
        );
        return res.data;
      })
      .catch(() => {
        dispatch(
          toggleNotification(
            i18n.t('notifications.stories.attachError'),
            'error'
          )
        );
      });
  };

  const onCancel = () => {
    navigate('/my-lns');
  };

  const onInsertStoryAsset = (asset: AssetDynamic) => {
    const imageAssetObj = { ...asset.assetI } as ImageAsset;
    imageAssetObj.positionInScript = dynamicAsset.length;
    const assetInput = [...dynamicAsset, { assetI: { ...imageAssetObj } }];
    onUpdateDynamicAssets(assetInput);
  };

  const saveBtnDisabled = !isFormValid(
    ['title', 'slug', 'genre', 'location'],
    state.formData
  );

  const onAddDialogue = (dialogueObj: AssetDynamic) => {
    onUpdateDynamicAssets([...dynamicAsset, dialogueObj]);
  };

  const onUnlockStory = () => {
    const formDataScripted = {
      ...state.formData
    };

    if (storyLock && storyStates.length > 0) {
      formDataScripted.storyState = storyStates[0].guid;
      setStoryInitState(storyStates[0]);
      setStoryLock(false);
      handleStoryUpdate(formDataScripted, true);
    }

    if (storyApproved) {
      setStoryApproved(false);
    }
  };

  const addAssignment = () => {
    const input = { ...defaultState, story: guid, storyObj: state.story };
    setEditAssignment(input);
  };

  const onSaveAssignmentForm = (formData: FormData) => {
    if (formData) {
      createAssignment(formData).then(() => {
        dispatch(
          toggleNotification(
            i18n.t('notifications.assignments.createSuccess'),
            'success'
          )
        );
      });
    }
    setEditAssignment(undefined);
  };

  const onCancelForm = () => {
    setEditAssignment(undefined);
  };

  const onChangeTabValue = (val: number) => {
    setTabValue(val);
  };

  const onApproveStory = () => {
    const approveState = storyStates.find(stateItem => {
      return stateItem.name === StoryState.approved;
    });

    if (approveState) {
      const updatedFormData = {
        ...state.formData,
        storyState: approveState.guid
      };
      setStoryApproved(true);
      handleStoryUpdate(updatedFormData, false);
    }
  };

  return (
    <>
      <Box display="flex" flexDirection="column" gridGap={8}>
        <ContentBoxItem title>
          <TitleWrapper
            title={state.formData?.title.toUpperCase()}
            icon={storyLock ? <Lock color="primary" /> : <></>}
          />
          <BtnWrapper>
            {(storyLock || storyApproved) && (isUserAdmin || isUserStoryOwner) && (
              <Button
                color="primary"
                variant="contained"
                onClick={onUnlockStory}
              >
                {i18n.t('pages.stories.helperText.unlockStory')}
              </Button>
            )}
            {!storyLock && !storyApproved && (
              <Button
                disabled={storyLock || storyApproved}
                color="primary"
                onClick={addAssignment}
              >
                {i18n.t('pages.assignments.assignments')}
              </Button>
            )}
            <Button color="secondary" onClick={onCancel}>
              {i18n.t('button.cancel')}
            </Button>
            <Button
              disabled={saveBtnDisabled || storyLock || storyApproved}
              color="primary"
              name="save"
              variant="outlined"
              onClick={onSave}
            >
              {i18n.t('button.save')}
            </Button>
            {canApprove && !storyApproved && !storyLock && (
              <Button
                disabled={storyLock}
                color="primary"
                variant="contained"
                onClick={onApproveStory}
              >
                {i18n.t('button.approve')}
              </Button>
            )}
          </BtnWrapper>
        </ContentBoxItem>
        <Box
          display="flex"
          flex-direction="row"
          alignItems="start"
          gridGap="24px"
        >
          <Box flex="2">
            <StoryFormTabs
              tabValue={tabValue}
              storyLock={storyLock || storyApproved}
              calculatedDuration={calculatedDuration}
              dynamicAssets={dynamicAsset}
              formData={state.formData}
              onChangeTabValue={onChangeTabValue}
              onAddDialogueAsset={onAddDialogue}
              onChangeField={onChangeField}
              onChangeType={onChangeType}
              onEditorChange={onEditorChange}
              onUpdateDynamicAssets={onUpdateDynamicAssets}
              story={guid}
            />
          </Box>

          <SideContent>
            <StoryBrowseAccordions
              storyData={{
                title: state.formData.title,
                guid: state.formData.guid,
                text: state.formData.text
              }}
              storyLock={storyLock || storyApproved}
              onUpdateAssets={onInsertStoryAsset}
            />
          </SideContent>
        </Box>
      </Box>

      {editAssignment && (
        <DialogForm
          isAdmin={isUserAdmin}
          currentLnsUser={currentLnsUser}
          assignment={editAssignment}
          onSave={onSaveAssignmentForm}
          onCancel={onCancelForm}
          taskStates={taskStates}
        />
      )}
    </>
  );
};

export default Edit;
